import { Input } from '@components'
import cn from '@integrations/classnames'
import type { FocusEvent } from '@integrations/react'
import { tr } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'
import QuestionnaireYesNoField from '../QuestionnaireYesNoField'

interface Props {
  formValues: UseFormValuesOutput
  handleSave: (event?: FocusEvent<HTMLElement> | string | any) => void
  countryOptions: {
    value: string
    label: string
  }[]
}

function QuestionnaireFourthStep({ formValues, handleSave, countryOptions }: Props) {
  return (
    <>
      <div className={cn('subtitle')}>
        {tr('FRONTEND.QUESTIONNAIRE.KYB.EXPECTED_ACCOUNT_ACTIVITY', 'Expected Account Activity')}
      </div>

      <Input
        type='text'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.PAYMENT_ACCOUNT_PURPOSE',
          `Explanation regarding the purpose of use of the account`
        )}
        {...formValues.registerInput('paymentAccountPurpose')}
        data-test='payment-account-purpose'
        onBlur={handleSave}
      />
      <Input
        type='text'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.REASON_OF_INCOMING_PAYMENTS',
          `Explanation on the underlying reason for incoming payment`
        )}
        {...formValues.registerInput('reasonOfIncomingPayments')}
        data-test='reason-of-incoming-payment'
        onBlur={handleSave}
      />
      <Input
        type='text'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.REASON_OF_OUTGOING_PAYMENTS',
          `Explanation on the underlying reason for outgoing payment`
        )}
        {...formValues.registerInput('reasonOfOutgoingPayments')}
        data-test='reason-of-outgoing-payment'
        onBlur={handleSave}
      />
      <Input
        type='text'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.SOURCE_OF_FUNDS', `Source of Funds into the account`)}
        {...formValues.registerInput('sourceOfFunds')}
        data-test='source-of-funds'
        onBlur={handleSave}
      />
      <Input
        type='text'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.TARGET_BUSINESS_SECTORS', 'Target business sectors')}
        {...formValues.registerInput('businessSectors')}
        data-test='business-sectors'
        onBlur={handleSave}
      />
      <Input
        type='select'
        isSearchable={true}
        isMultiSelect={true}
        options={countryOptions}
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.COUNTRIES_PAYMENTS_PROCESSED',
          'Countries from which payments will be processed'
        )}
        {...formValues.registerInput('countriesFromPaymentsWillBeProcessed')}
        data-test='countries-from-payments-will-be-processed'
        onBlur={() => handleSave('countriesFromPaymentsWillBeProcessed')}
      />
      <Input
        type='select'
        isSearchable={true}
        isMultiSelect={true}
        options={countryOptions}
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.COUNTRIES_TO_PAYMENTS_PROCESSED',
          'Countries to which payments will be processed'
        )}
        {...formValues.registerInput('countriesToPaymentsWillBeProcessed')}
        data-test='countries-to-payments-will-be-processed'
        onBlur={() => handleSave('countriesToPaymentsWillBeProcessed')}
      />
      <Input
        type='text'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.COUNTRIES_CUSTOMER_ARE_BASED',
          `Percentage breakdown of the countries in which your customers are based`
        )}
        {...formValues.registerInput('percentageBreakdownOfTheCountriesCustomersAreBase')}
        data-test='percentage-breakdown-of-the-countries-customers-are-base'
        onBlur={handleSave}
      />
      <Input
        type='number'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.INCOMING_TRANSACTION_VOLUME_IN_EUR',
          `Expected incoming transactions volume in EUR on a monthly basis`
        )}
        {...formValues.registerInput('expectedIncomingEURTransactionsMonthly')}
        data-test='expected-incoming-eur-transactions-monthly'
        onBlur={handleSave}
      />
      <Input
        type='number'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.EXPECTED_INCOMING_TRANSACTIONS_MONTHLY',
          `Expected incoming transactions value on a monthly basis`
        )}
        {...formValues.registerInput('expectedIncomingTransactionsMonthly')}
        data-test='expected-incoming-transactions-monthly'
        onBlur={handleSave}
      />
      <Input
        type='number'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.SINGLE_INCOMING_TRANSACTIONS_IN_EUR',
          `Expected average amount of single incoming transaction in EUR`
        )}
        {...formValues.registerInput('expectedAverageAmountOfSingleIncomingTransactionInEUR')}
        data-test='expected-amount-of-single-incoming-transaction-in-eur'
        onBlur={handleSave}
      />
      <Input
        type='number'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.EXPECTED_OUTGOING_TRANSACTIONS_IN_EUR_MONTHLY',
          `Expected outgoing transactions volume in EUR on a monthly basis`
        )}
        {...formValues.registerInput('expectedOutgoingTransactionsInEURMonthly')}
        data-test='expected-outgoing-transactions-in-eur-monthly'
        onBlur={handleSave}
      />
      <Input
        type='number'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.EXPECTED_OUTGOING_TRANSACTIONS_MONTHLY',
          `Expected outgoing transactions value on a monthly basis`
        )}
        {...formValues.registerInput('expectedOutgoingTransactionsMonthly')}
        data-test='expected-outgoing-transactions-monthly'
        onBlur={handleSave}
      />
      <Input
        type='number'
        label={tr(
          'FRONTEND.QUESTIONNAIRE.KYB.COMPANY_REGISTRATION_NUMBER',
          `Expected average amount of single outgoing transaction in EUR`
        )}
        {...formValues.registerInput('expectedAverageAmountOfSingleOutgoingTransactionEUR')}
        data-test='expected-average-amount-of-single-outgoing-transaction-in-eur'
        onBlur={handleSave}
      />

      <QuestionnaireYesNoField
        heading={tr('FRONTEND.QUESTIONNAIRE.KYB.CURRENT_PAYMENT_SOLUTION', 'Current payment solution providers.')}
        selected={formValues.getValue('paymentSolutionProviders')}
        setSelected={(value: string) => formValues.setValue('paymentSolutionProviders', value)}
      />
      <Input
        type='text'
        label={tr('FRONTEND.QUESTIONNAIRE.KYB.WHERE_DID_YOU_HEAR_ABOUT_US', `Where did you hear about us?`)}
        {...formValues.registerInput('whereDidYouHearAboutUs')}
        data-test='where-did-you-hear-about-us'
      />
    </>
  )
}

export default QuestionnaireFourthStep
